'use client';

import { useReducedMotion } from 'motion/react';

import { cn } from '@/lib/utils';

export type ResponsiveVideoProps = {
  src: string;
  poster: string;
  width: number;
  height: number;
  autoplay?: boolean;
  className?: string;
  caption?: string;
};

/**
 * Direct-file video with poster-first policy; autoplay never required.
 */
export function ResponsiveVideo({
  src,
  poster,
  width,
  height,
  autoplay = false,
  className,
  caption,
}: ResponsiveVideoProps) {
  const reducedMotion = useReducedMotion() ?? false;
  const allowAutoplay = autoplay && !reducedMotion;

  return (
    <figure className="my-8">
      <video
        src={src}
        poster={poster}
        width={width}
        height={height}
        controls
        playsInline
        preload="metadata"
        autoPlay={allowAutoplay}
        muted={allowAutoplay}
        className={cn(
          'border-border h-auto w-full max-w-full rounded-xl border',
          className
        )}
      >
        <track kind="captions" />
      </video>
      {caption && (
        <figcaption className="text-muted-foreground mt-2 font-mono text-xs">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

type BackgroundVideoProps = {
  mp4Src: string;
  webmSrc?: string;
  posterSrc?: string;
  className?: string;
};

/**
 * Decorative hero background — aria-hidden, suppressed under reduced motion.
 */
export function BackgroundVideo({
  mp4Src,
  webmSrc,
  posterSrc,
  className,
}: BackgroundVideoProps) {
  const reducedMotion = useReducedMotion() ?? false;

  if (reducedMotion) {
    return (
      <div
        aria-hidden="true"
        className={cn(
          'from-background via-background/90 to-background/40 absolute inset-0 -z-20 bg-gradient-to-b',
          className
        )}
      />
    );
  }

  return (
    <video
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      poster={posterSrc}
      aria-hidden="true"
      className={cn(
        'absolute inset-0 -z-20 size-full object-cover object-center',
        className
      )}
    >
      {webmSrc && <source src={webmSrc} type="video/webm" />}
      <source src={mp4Src} type="video/mp4" />
    </video>
  );
}
